import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';

import classnames from 'classnames';
import Shelf from '../components/widgets/Shelf';
import { Checkbox, Label } from '../components/widgets';
import AutoComplete from '../components/molecules/AutoComplete';
import css from './widgetShelf.css';
import NewTagInput from '../components/widgets/NewTagInput';
import Spinner from '../components/widgets/Spinner';
import TextField from '../components/molecules/TextField';
import FilterFields from '../components/partials/FilterFields';
import AxisSettings from './AxisSettings/AxisSettings';
import { isRangeAxisChart } from '../components/partials/PivotChart/axisConfig';
import useParseDimsMetricsAndFilters from './widgetFields';
import { DataTypes, RESTRICTION_TYPE, WIDGET_DATA_TYPES } from './widgetDataTypes';
import { getAdminModeEnabled, getUserData } from '../selectors/user';
import Dropdown from '../components/molecules/Dropdown';
import { validateName } from '../components/utilities/Sanitize';
import ColumnPreview from '../resources/png/dashboards/previews/column.png';
import BarPreview from '../resources/png/dashboards/previews/bar.png';
import StackedPreview from '../resources/png/dashboards/previews/stacked.png';
import LinePreview from '../resources/png/dashboards/previews/line.png';
import PiePreview from '../resources/png/dashboards/previews/pie.png';
import PivotPreview from '../resources/png/dashboards/previews/pivot.png';
import TablePreview from '../resources/png/dashboards/previews/table.png';
import TotalsPreview from '../resources/png/dashboards/previews/totals.svg';
import HeatmapPreview from '../resources/png/dashboards/previews/heatmap.png';
import Tooltip from '../components/molecules/Tooltip';
import {
    DAY_DIMENSION,
    DEFAULT_SKAN_DATE_TYPE,
    HAS_CREATIVE_PREVIEW_FIELD,
    HAS_CREATIVE_PREVIEW_VALUE,
    requestParams,
    trackDashboardsWidgetOpenedMixpanelEvent,
} from './utils';
import { WIDGET_COLUMNS, WIDGET_DIMENSIONS, WIDGET_METRICS, WIDGET_ROWS } from './consts';
import { CheckboxVariants } from '../components/atoms/Checkbox';
import Icon, { IconSizes, IconTypes, IconVariants } from '../components/foundations/Icon';
import {
    CUSTOM_DASHBOARD_CHART_TYPES,
    HEATMAP_WIDGET,
    OPERATOR_TYPES,
    WIDGET_TYPE_OPTIONS,
    WIDGET_TYPE_TOTALS,
    WIDGET_TYPES,
} from '../utils/dashboardConsts';
import { getFiltersWithTags } from '../utils/dashboardUtils';
import HeatmapColorConfig from '../components/partials/HeatmapColorConfig/HeatmapColorConfig';
import { COLOR_MODES, defaultHeatmapOptions, normalizeHex } from './heatmap/heatmapOptions';

const SKAN_DATE_TYPES = {
    skan_postback_date: 'Postback Date',
    estimated_install_date: 'Estimated Install Date',
};
const DATE_TYPE_OPTIONS = Object.keys(SKAN_DATE_TYPES).map(k => ({ label: SKAN_DATE_TYPES[k], value: k }));

const CHART_TYPES_MULTI_METRICS = [
    { name: 'column', display_name: 'Bar Chart (Vertical)' },
    { name: 'bar', display_name: 'Bar Chart (Horizontal)' },
    { name: 'line', display_name: 'Line Chart' },
];

const MAX_METRICS_PER_WIDGET = 3;

const WIDGET_TYPE_TO_PREVIEW = {
    column: ColumnPreview,
    bar: BarPreview,
    stacked: StackedPreview,
    line: LinePreview,
    pie: PiePreview,
    pivot: PivotPreview,
    table: TablePreview,
    totals: TotalsPreview,
    heatmap: HeatmapPreview,
};

const SUBCOMPONENTS = {
    name: 'name',
    dimensions: 'dimensions',
    rows: 'rows',
    columns: 'columns',
    metrics: 'metrics',
    unifiedDataType: 'unifiedDataType',
    crossDeviceDataType: 'crossDeviceDataType',
    heatmapColor: 'heatmapColor',
    axis: 'axis',
};

const IMAGE_GROUP_BY = 'image';

const WIDGETS_WITH_CREATIVE_PREVIEW = ['table', 'pivot'];

const shouldShowCreativePreviewCheckbox = (type, dataType) =>
    dataType === DataTypes.CREATIVES && WIDGETS_WITH_CREATIVE_PREVIEW.includes(type);

const hasAIDimension = dimensions =>
    dimensions.some(dim => dim.startsWith(`${requestParams.CREATIVES_AI_DIMENSIONS}.`));

const ensureCreativePreviewFilter = (filters, dimensions) => {
    if (!hasAIDimension(dimensions) || filters.some(f => f.field === HAS_CREATIVE_PREVIEW_FIELD)) return filters;
    return [...filters, { field: HAS_CREATIVE_PREVIEW_FIELD, operator: 'in', values: [HAS_CREATIVE_PREVIEW_VALUE] }];
};

const fieldsEmptyState = {
    query: {
        dimensions: [],
        metrics: [],
        filters: [],
        creativeGroupBy: [],
        dataTypeFields: [],
    },
    options: {
        rows: [],
        columns: [],
    },
};

function getFieldsLabelClass(isMandatory) {
    return classnames({
        [css.fieldLabel]: true,
        [css.fieldLabelRequired]: isMandatory,
    });
}

function useRetrieveFieldObjectsByFieldNames(fieldNames, fieldObjects) {
    return useMemo(() => fieldNames.map(name => fieldObjects.find(x => x.value === name)).filter(x => x), [
        fieldNames,
        fieldObjects,
    ]);
}

const TRANSLATION_PREFIX = 'STATIC.PAGES.DASHBOARD.SHELF';

function WidgetShelf({
    adminMode,
    userData,
    widgets,
    widget: _widget,
    close,
    apiFields = null,
    onUpdateWidget,
    preconfiguredType,
    translate = () => {},
}) {
    const [shelfOpen, setShelfOpen] = useState(false);
    const [widget, setWidget] = useState(_widget);
    const originalWidget = useRef(widget);

    useEffect(() => {
        setShelfOpen(true);
    }, []);

    const [
        allDimensions,
        allMetrics,
        allFilters,
        unifiedDataTypes,
        crossDeviceDataTypes,
    ] = useParseDimsMetricsAndFilters(apiFields, adminMode, userData, widget.dataType);

    const availableDataTypes = useRef(
        Object.values(WIDGET_DATA_TYPES).filter(
            dataType =>
                !dataType.feature_flags || dataType.feature_flags.some(flag => userData.feature_flags.includes(flag))
        )
    );

    // Translate the widget's dimensions, metrics (and options.rows / options.columns), which are simply list of
    // api field names, into a list of objects that look like: [{value: 'xxx', label: 'XXX'}, ...]
    const selectedDimensionsUI = useRetrieveFieldObjectsByFieldNames(widget.query.dimensions, allDimensions);
    const selectedRowsUI = useRetrieveFieldObjectsByFieldNames(widget.options.rows, allDimensions);
    const selectedColumnsUI = useRetrieveFieldObjectsByFieldNames(widget.options.columns, allDimensions);
    const selectedMetricsUI = useRetrieveFieldObjectsByFieldNames(widget.query.metrics, allMetrics);
    const selectedDataTypesUI = useRetrieveFieldObjectsByFieldNames(widget.query.dataTypeFields, unifiedDataTypes);
    const crossDeviceDataTypesUI = useRetrieveFieldObjectsByFieldNames(
        widget.query.dataTypeFields,
        crossDeviceDataTypes
    );

    const axisMetrics = useMemo(
        () =>
            selectedMetricsUI.map(metric => ({ name: metric.name, display_name: metric.display_name || metric.label })),
        [selectedMetricsUI]
    );

    const selectedFiltersWithTags = useMemo(() => {
        return getFiltersWithTags(widget.query.filters, allFilters);
    }, [widget.query.filters, allFilters]);

    // If we already used a filter (in widget.query.filters) then we don't want it to be an option again
    const allUnusedFilters = useMemo(() => {
        const usedFilters = widget.query.filters.map(x => x.field);
        return allFilters.filter(filter => !usedFilters.includes(filter.name));
    }, [widget.query.filters, allFilters]);

    const queryWithAIFilter = (prevDimensions, newDimensions, query) =>
        !hasAIDimension(prevDimensions) && hasAIDimension(newDimensions)
            ? { ...query, filters: ensureCreativePreviewFilter(query.filters, newDimensions) }
            : query;

    function updateWidgetQueryFields(updatedQueryFields) {
        setWidget(prevWidget => {
            const newQuery = {
                ...prevWidget.query,
                ...updatedQueryFields,
            };
            return {
                ...prevWidget,
                query: updatedQueryFields.dimensions
                    ? queryWithAIFilter(prevWidget.query.dimensions, updatedQueryFields.dimensions, newQuery)
                    : newQuery,
            };
        });
    }

    function setHeatmapOptions(newHeatmapOptions) {
        setWidget(prevWidget => ({ ...prevWidget, heatMapChartOptions: newHeatmapOptions }));
    }

    function setWidgetOptions(newOptions) {
        setWidget(prevWidget => {
            const prevDims = [...prevWidget.options.rows, ...prevWidget.options.columns];
            const newDims = [
                ...(newOptions.rows || prevWidget.options.rows),
                ...(newOptions.columns || prevWidget.options.columns),
            ];
            return {
                ...prevWidget,
                query: queryWithAIFilter(prevDims, newDims, prevWidget.query),
                options: {
                    ...prevWidget.options,
                    ...newOptions,
                },
            };
        });
    }

    // Drop axis side overrides for metrics that are no longer selected, so the
    // persisted options.axis.sides never keeps stale metric entries.
    useEffect(() => {
        const { axis } = widget.options;
        if (!axis || !axis.sides) {
            return;
        }
        const selected = new Set(widget.query.metrics);
        const staleKeys = Object.keys(axis.sides).filter(name => !selected.has(name));
        if (staleKeys.length === 0) {
            return;
        }
        const sides = { ...axis.sides };
        staleKeys.forEach(name => delete sides[name]);
        setWidgetOptions({ axis: { ...axis, sides } });
    }, [widget.query.metrics, widget.options.axis]);

    function setWidgetQueryFieldsAndOptions(updatedQueryFields, newOptions) {
        setWidget(prevWidget => {
            const newQuery = {
                ...prevWidget.query,
                ...updatedQueryFields,
            };
            const prevDims = [
                ...prevWidget.query.dimensions,
                ...prevWidget.options.rows,
                ...prevWidget.options.columns,
            ];
            const newDims = [
                ...(updatedQueryFields.dimensions || prevWidget.query.dimensions),
                ...(newOptions.rows || prevWidget.options.rows),
                ...(newOptions.columns || prevWidget.options.columns),
            ];
            return {
                ...prevWidget,
                query: queryWithAIFilter(prevDims, newDims, newQuery),
                options: {
                    ...prevWidget.options,
                    ...newOptions,
                },
            };
        });
    }

    // This function is getting the widget's selected dimensions, metrics and data type
    // and a restriction type (blacklist/whitelist). It returns null if all selected metrics/dimensions are valid, or
    // returns json of metric and dimension that must have come together (whitelist),
    // or shouldn't come together (blacklist)

    function getCollisionFieldsRestrictions(dimensions, dataType) {
        const { restrictions } = WIDGET_DATA_TYPES[dataType];
        let errorDetails = null;

        if (!restrictions || !restrictions[RESTRICTION_TYPE.COLLISION]) return errorDetails;

        restrictions[RESTRICTION_TYPE.COLLISION].some(({ firstDimensionsGroup, secondDimensionsGroup, names }) => {
            const firstGroupDimensions = dimensions.filter(dimension => secondDimensionsGroup.includes(dimension));
            const secondGroupDimensions = dimensions.filter(dimension => firstDimensionsGroup.includes(dimension));

            if (!firstGroupDimensions.length || !secondGroupDimensions.length) return false;
            errorDetails = {
                firstGroupDimensions: firstGroupDimensions.map(dimension => ({
                    value: dimension,
                    label: names[dimension],
                })),
                secondGroupDimensions: secondGroupDimensions.map(dimension => ({
                    value: dimension,
                    label: names[dimension],
                })),
            };
            return true;
        });

        return errorDetails;
    }

    function getFieldsRestrictions(dimensions, metrics, dataType, restrictionType) {
        const metricsGroups = allMetrics.filter(metric => metrics.includes(metric.name)).map(metric => metric.group);
        const { restrictions } = WIDGET_DATA_TYPES[dataType];
        if (!restrictions || !restrictions[restrictionType] || !dimensions.length || !metrics.length) {
            return null;
        }

        for (let i = 0; i < restrictions[restrictionType].length; i++) {
            const restriction = restrictions[restrictionType][i];

            for (let j = 0; j < restriction.metrics.length; j++) {
                const restrictionMetric = restriction.metrics[j];

                if (!metrics.includes(restrictionMetric) && !metricsGroups.includes(restrictionMetric)) {
                    continue;
                }

                for (let k = 0; k < restriction.dimensions.length; k++) {
                    const restrictionDimension = restriction.dimensions[k];
                    const isRestrictedDimensionSelected = dimensions.includes(restrictionDimension);

                    if (
                        (restrictionType === RESTRICTION_TYPE.BLACKLIST && isRestrictedDimensionSelected) ||
                        (restrictionType === RESTRICTION_TYPE.WHITELIST && !isRestrictedDimensionSelected)
                    ) {
                        return {
                            metric: restrictions.names[restrictionMetric],
                            dimension: restrictions.names[restrictionDimension],
                        };
                    }
                }
            }
        }

        return null;
    }

    const isMultiMetricsChartWidget = !!CHART_TYPES_MULTI_METRICS.find(chart => chart.name === widget.type);
    const isChartWidget = CUSTOM_DASHBOARD_CHART_TYPES.find(x => x.name === widget.type);
    const isSingleMetricWidget = widget.type === WIDGET_TYPE_TOTALS || widget.type === HEATMAP_WIDGET;
    const isMultipleMetricsWidget = (isMultiMetricsChartWidget || !isChartWidget) && !isSingleMetricWidget;

    const [error, setError] = useState('Gotta set some values...');
    const [errorComponent, setErrorComponent] = useState(null);
    const didUpdateSubComponent = useRef({});

    const [breakdownsDisabled, setBreakdownsDisabled] = useState(
        isMultiMetricsChartWidget && widget.query.metrics.length > 1
    );
    const [breakdownColumnsError, setBreakdownColumnsError] = useState(
        breakdownsDisabled ? `${TRANSLATION_PREFIX}.ERRORS.BREAKDOWN_DISABLED` : null
    );
    const [metricsError, setMetricsError] = useState(null);

    const resetErrorToDefault = () => {
        setError('Gotta set some values...');
        setErrorComponent(null);
    };

    const disableBreakdowns = () => {
        setBreakdownsDisabled(true);
        setBreakdownColumnsError(`${TRANSLATION_PREFIX}.ERRORS.BREAKDOWN_DISABLED`);
    };

    const enableBreakdowns = () => {
        setBreakdownsDisabled(false);
        setBreakdownColumnsError(null);
    };

    useEffect(() => {
        if (!isMultipleMetricsWidget && widget.query.metrics.length > 1) {
            updateWidgetQueryFields({ metrics: [widget.query.metrics[0]] });
            enableBreakdowns();
        }
    }, [isMultipleMetricsWidget, widget.type, widget.query.metrics]);

    useEffect(() => {
        const { name, type, options, query } = widget;

        if (!name.length) {
            if (!didUpdateSubComponent.current[SUBCOMPONENTS.name]) {
                resetErrorToDefault();
                return;
            }

            setError('Please enter a widget name');
            setErrorComponent(SUBCOMPONENTS.name);
            return;
        } else {
            didUpdateSubComponent.current[SUBCOMPONENTS.name] = true;
        }

        if (!validateName(name)) {
            setError('Invalid character. Try a different name');
            setErrorComponent(SUBCOMPONENTS.name);
            return;
        }

        if (widgets.some(w => w.gridParams.i !== widget.gridParams.i && w.name === name)) {
            setError('A widget with that name already exists');
            setErrorComponent(SUBCOMPONENTS.name);
            return;
        }

        if (WIDGET_DIMENSIONS[type]?.mandatory && !query.dimensions.length) {
            if (!didUpdateSubComponent.current[SUBCOMPONENTS.dimensions]) {
                resetErrorToDefault();
                return;
            }

            setError('You must select at least one dimension');
            setErrorComponent(SUBCOMPONENTS.dimensions);
            return;
        } else {
            didUpdateSubComponent.current[SUBCOMPONENTS.dimensions] = true;
        }
        if (widget.dataType === DataTypes.CROSS_DEVICE && !query.dataTypeFields.length) {
            if (!didUpdateSubComponent.current[SUBCOMPONENTS.crossDeviceDataType]) {
                resetErrorToDefault();
                return;
            }
            setError('You must select an attribution type');
            setErrorComponent(SUBCOMPONENTS.crossDeviceDataType);
            return;
        } else {
            didUpdateSubComponent.current[SUBCOMPONENTS.crossDeviceDataType] = true;
        }
        if (widget.dataType === DataTypes.UNIFIED && !query.dataTypeFields.length) {
            if (!didUpdateSubComponent.current[SUBCOMPONENTS.unifiedDataType]) {
                resetErrorToDefault();
                return;
            }

            setError('You must select at least one data type');
            setErrorComponent(SUBCOMPONENTS.unifiedDataType);
            return;
        } else {
            didUpdateSubComponent.current[SUBCOMPONENTS.unifiedDataType] = true;
        }

        if (WIDGET_COLUMNS[type]?.mandatory && !options.columns.length) {
            if (!didUpdateSubComponent.current[SUBCOMPONENTS.columns]) {
                resetErrorToDefault();
                return;
            }

            setError(
                WIDGET_COLUMNS[type].single
                    ? translate(`${TRANSLATION_PREFIX}.ERRORS.SELECT_Y_DIMENSION`)
                    : translate(`${TRANSLATION_PREFIX}.ERRORS.SELECT_COLUMN`)
            );
            setErrorComponent(SUBCOMPONENTS.columns);
            return;
        } else {
            didUpdateSubComponent.current[SUBCOMPONENTS.columns] = true;
        }

        if (WIDGET_ROWS[type]?.mandatory && !options.rows.length) {
            if (!didUpdateSubComponent.current[SUBCOMPONENTS.rows]) {
                resetErrorToDefault();
                return;
            }

            setError(
                WIDGET_ROWS[type].single
                    ? translate(`${TRANSLATION_PREFIX}.ERRORS.SELECT_X_DIMENSION`)
                    : translate(`${TRANSLATION_PREFIX}.ERRORS.SELECT_ROW`)
            );
            setErrorComponent(SUBCOMPONENTS.rows);
            return;
        } else {
            didUpdateSubComponent.current[SUBCOMPONENTS.rows] = true;
        }

        if (WIDGET_ROWS[type]?.single && options.rows.length > 1) {
            setError(translate(`${TRANSLATION_PREFIX}.ERRORS.ONE_X_DIMENSION`));
            setErrorComponent(SUBCOMPONENTS.rows);
            return;
        }

        if (WIDGET_COLUMNS[type]?.single && options.columns.length > 1) {
            setError(translate(`${TRANSLATION_PREFIX}.ERRORS.ONE_Y_DIMENSION`));
            setErrorComponent(SUBCOMPONENTS.columns);
            return;
        }

        if (isSingleMetricWidget && query.metrics.length > 1) {
            setError(translate(`${TRANSLATION_PREFIX}.ERRORS.ONE_METRIC`));
            setErrorComponent(SUBCOMPONENTS.metrics);
            return;
        }

        if (WIDGET_METRICS[type]?.mandatory && !query.metrics.length) {
            if (!didUpdateSubComponent.current[SUBCOMPONENTS.metrics]) {
                resetErrorToDefault();
                return;
            }

            setError(`You must select ${isSingleMetricWidget ? 'one' : 'at least one'} metric`);
            setErrorComponent(SUBCOMPONENTS.metrics);
            return;
        } else {
            const dimensions = query.dimensions.concat(options.rows.concat(options.columns));

            const whitelistRestriction = getFieldsRestrictions(
                dimensions,
                query.metrics,
                widget.dataType,
                RESTRICTION_TYPE.WHITELIST
            );

            if (whitelistRestriction) {
                setError(
                    `${whitelistRestriction.metric} is available only if the dimension ${whitelistRestriction.dimension} is also selected`
                );
                setErrorComponent(SUBCOMPONENTS.metrics);
                return;
            }

            const blacklistRestriction = getFieldsRestrictions(
                dimensions,
                query.metrics,
                widget.dataType,
                RESTRICTION_TYPE.BLACKLIST
            );

            if (blacklistRestriction) {
                setError(
                    `${blacklistRestriction.metric} can not be selected with the dimension ${blacklistRestriction.dimension}`
                );
                setErrorComponent(SUBCOMPONENTS.metrics);
                return;
            }

            const collisionRestriction = getCollisionFieldsRestrictions(dimensions, widget.dataType);

            if (collisionRestriction) {
                const { firstGroupDimensions, secondGroupDimensions } = collisionRestriction;
                setError(
                    `${firstGroupDimensions
                        .map(({ label }) => label)
                        .join('/')} cannot be combined with ${secondGroupDimensions
                        .map(({ label }) => label)
                        .join('/')}`
                );
                const firstDimension = firstGroupDimensions[0].value;
                setErrorComponent(
                    options.rows.includes(firstDimension)
                        ? SUBCOMPONENTS.rows
                        : options.columns.includes(firstDimension)
                        ? SUBCOMPONENTS.columns
                        : SUBCOMPONENTS.dimensions
                );
                return;
            }

            didUpdateSubComponent.current[SUBCOMPONENTS.metrics] = true;
        }

        if (type === HEATMAP_WIDGET) {
            const heatmapOptions = widget.heatMapChartOptions || defaultHeatmapOptions();
            const colorMode = heatmapOptions.mode || COLOR_MODES.GRADIENT;
            const hasClasses = heatmapOptions.dataClasses && heatmapOptions.dataClasses.length > 0;
            if (colorMode === COLOR_MODES.BRACKETS) {
                if (!hasClasses) {
                    setError(translate(`${TRANSLATION_PREFIX}.ERRORS.BRACKET_MIN`));
                    setErrorComponent(SUBCOMPONENTS.heatmapColor);
                    return;
                }
                const hasBound = v => typeof v === 'number' && !Number.isNaN(v);
                const emptyBracket = heatmapOptions.dataClasses.some(dc => !hasBound(dc.from) && !hasBound(dc.to));
                if (emptyBracket) {
                    setError(translate(`${TRANSLATION_PREFIX}.ERRORS.BRACKET_BOUND`));
                    setErrorComponent(SUBCOMPONENTS.heatmapColor);
                    return;
                }
                const bracketColors = heatmapOptions.dataClasses.map(dc => normalizeHex(dc.color));
                if (bracketColors.some((c, i) => bracketColors.indexOf(c) !== i)) {
                    setError(translate(`${TRANSLATION_PREFIX}.ERRORS.BRACKET_COLOR_UNIQUE`));
                    setErrorComponent(SUBCOMPONENTS.heatmapColor);
                    return;
                }
            }
            if (colorMode === COLOR_MODES.GRADIENT) {
                if (!heatmapOptions.stops || heatmapOptions.stops.length < 2) {
                    setError(translate(`${TRANSLATION_PREFIX}.ERRORS.GRADIENT_MIN_STOPS`));
                    setErrorComponent(SUBCOMPONENTS.heatmapColor);
                    return;
                }
                const hasBlankStop = heatmapOptions.stops.some(s => typeof s.stop !== 'number' || Number.isNaN(s.stop));
                if (hasBlankStop) {
                    setError(translate(`${TRANSLATION_PREFIX}.ERRORS.GRADIENT_STOP_VALUE`));
                    setErrorComponent(SUBCOMPONENTS.heatmapColor);
                    return;
                }
                const stopValues = heatmapOptions.stops.map(s => s.stop);
                const hasDuplicateStop = stopValues.some((v, i) => stopValues.indexOf(v) !== i);
                if (hasDuplicateStop) {
                    setError(translate(`${TRANSLATION_PREFIX}.ERRORS.GRADIENT_STOP_UNIQUE`));
                    setErrorComponent(SUBCOMPONENTS.heatmapColor);
                    return;
                }
                const stopColors = heatmapOptions.stops.map(s => normalizeHex(s.color));
                if (stopColors.some((c, i) => stopColors.indexOf(c) !== i)) {
                    setError(translate(`${TRANSLATION_PREFIX}.ERRORS.GRADIENT_COLOR_UNIQUE`));
                    setErrorComponent(SUBCOMPONENTS.heatmapColor);
                    return;
                }
            }
        }

        const { axis: axisCfg } = widget.options;
        if (isRangeAxisChart(widget.type) && axisCfg) {
            const rangeInvalid = r =>
                r && r.min !== null && r.min !== undefined && r.max !== null && r.max !== undefined && r.min > r.max;
            if (rangeInvalid(axisCfg.range) || rangeInvalid(axisCfg.left) || rangeInvalid(axisCfg.right)) {
                setError(`${TRANSLATION_PREFIX}.ERRORS.AXIS_MIN_MAX`);
                setErrorComponent(SUBCOMPONENTS.axis);
                return;
            }
        }

        setError('');
    }, [widget.name, widget.type, widget.options, widget.query, widget.heatMapChartOptions]);

    const prevDataType = useRef(widget.dataType);
    useEffect(() => {
        const { dataType, query } = widget;
        const isSkanWidget = dataType === DataTypes.SKAN || dataType === DataTypes.SKAN_RAW;

        if (prevDataType.current === dataType) return;
        prevDataType.current = dataType;

        if (isSkanWidget && !query.skanDateDimensionName) {
            updateWidgetQueryFields({ skanDateDimensionName: DEFAULT_SKAN_DATE_TYPE });
        } else if (!isSkanWidget) {
            updateWidgetQueryFields({ skanDateDimensionName: '' });
        }
    }, [widget.dataType]);

    function getNonEmptyQueryFilters(queryFilters) {
        return queryFilters.filter(queryFilter => queryFilter.values.length);
    }

    const onSave = useCallback(() => {
        const { type, options, query: widgetQuery } = widget;

        // Only table/pivot widgets persist column widths; for other types strip any stale width options
        // (e.g. left over after a type switch) so charts don't carry persistColumnWidths/columnWidths.
        const isTableLike = ['table', 'pivot'].includes(type);
        const persist = options.persistColumnWidths !== false;
        let widthOptions;
        if (!isTableLike) {
            widthOptions = { persistColumnWidths: undefined, columnWidths: undefined };
        } else if (persist) {
            widthOptions = {
                persistColumnWidths: true,
                ...(options.columnWidths ? { columnWidths: options.columnWidths } : {}),
            };
        } else {
            widthOptions = { persistColumnWidths: false, columnWidths: undefined };
        }

        const filters = getNonEmptyQueryFilters(widgetQuery.filters);
        const query = {
            ...widgetQuery,
            filters,
        };

        if (type === WIDGET_TYPE_TOTALS) {
            onUpdateWidget({
                ...widget,
                query: {
                    ...query,
                    isTotals: true,
                },
            });
        }

        if (WIDGET_DIMENSIONS[type]) {
            onUpdateWidget({
                ...widget,
                query,
                options: {
                    rows: [],
                    columns: [],
                    ...widthOptions,
                },
            });
        }

        if (WIDGET_ROWS[type]) {
            const rowsAndCols = [...options.rows, ...options.columns];
            onUpdateWidget({
                ...widget,
                query: {
                    ...query,
                    dimensions: rowsAndCols,
                },
                options: {
                    ...options,
                    ...widthOptions,
                },
            });
        }

        setError('');
        close();
    }, [widget]);

    const onFieldSelection = (field, index) => {
        const newFilters = [...widget.query.filters];
        newFilters[index] = {
            field: field.name,
            operator: OPERATOR_TYPES[0].name,
            values: [],
        };
        updateWidgetQueryFields({ filters: newFilters });
    };

    const onOperatorSelection = (operator, index) => {
        const newFilters = [...widget.query.filters];
        newFilters[index] = {
            ...newFilters[index],
            operator: operator.name,
        };
        updateWidgetQueryFields({ filters: newFilters });
    };

    const onFilterValueAdded = (tag, index) => {
        // tag is using display name, and we want to store names
        const filterValueObj = selectedFiltersWithTags[index].field.values.find(x => x.display_name === tag);

        if (filterValueObj && !widget.query.filters[index].values.includes(filterValueObj.name)) {
            const newFilters = [...widget.query.filters];
            newFilters[index] = {
                ...newFilters[index],
                values: [...newFilters[index].values, filterValueObj.name],
            };
            updateWidgetQueryFields({ filters: newFilters });
        }
    };

    const onFilterValueRemoved = (tag, index) => {
        // tag is using display name, and we want to store names
        const filterValueObj = selectedFiltersWithTags[index].field.values.find(x => x.display_name === tag);

        if (filterValueObj && widget.query.filters[index].values.includes(filterValueObj.name)) {
            const newFilters = [...widget.query.filters];
            newFilters[index] = {
                ...newFilters[index],
                values: newFilters[index].values.filter(x => x !== filterValueObj.name),
            };
            updateWidgetQueryFields({ filters: newFilters });
        }
    };

    const onMetricsChange = updatedMetrics => {
        if (!updatedMetrics) {
            updateWidgetQueryFields({ metrics: [] });
            return;
        }

        if (isSingleMetricWidget) {
            // Single-metric widgets (totals, heatmap) allow only one metric.
            const metricValue = Array.isArray(updatedMetrics) ? updatedMetrics[0]?.value : updatedMetrics.value;
            updateWidgetQueryFields({ metrics: metricValue ? [metricValue] : [] });
            return;
        }

        if (isChartWidget && !isMultiMetricsChartWidget) {
            updateWidgetQueryFields({ metrics: [updatedMetrics.value] });
            return;
        }

        if (isMultiMetricsChartWidget) {
            if (updatedMetrics.length > MAX_METRICS_PER_WIDGET) {
                setMetricsError(`${TRANSLATION_PREFIX}.ERRORS.SELECT_METRICS_MAX`);
            } else if (updatedMetrics.length > 1) {
                // set metrics and remove breakdowns
                setWidgetQueryFieldsAndOptions({ metrics: updatedMetrics.map(x => x.value) }, { columns: [] });
                setMetricsError(null);
                disableBreakdowns();
            } else if (updatedMetrics.length <= 1) {
                updateWidgetQueryFields({ metrics: updatedMetrics.map(x => x.value) });
                enableBreakdowns();
            }
        } else {
            // if it's a table/pivot table widget
            updateWidgetQueryFields({ metrics: updatedMetrics.map(x => x.value) });
        }
    };

    const onAddFilter = () =>
        updateWidgetQueryFields({
            filters: [
                ...widget.query.filters,
                {
                    field: 'default',
                    operator: 'in',
                    values: [],
                },
            ],
        });

    const onRemoveFilter = index => {
        const newFilters = [...widget.query.filters];
        newFilters.splice(index, 1);
        updateWidgetQueryFields({ filters: newFilters });
    };

    const isPivotWidget = widget.type === 'pivot';
    const dragMessage = `Drag tags to set ${isChartWidget ? 'label' : 'column'} order`;

    const dragTooltip = (
        <Tooltip interactive title={dragMessage}>
            <Icon
                name="question-line"
                type={IconTypes.MINGCUTE}
                className={css.tooltip}
                variant={IconVariants.LIGHT}
                size={IconSizes.X_SMALL}
            />
        </Tooltip>
    );

    const widgetColumns = WIDGET_COLUMNS[widget.type] && (
        <>
            {!WIDGET_COLUMNS[widget.type].mandatory && !isPivotWidget && <div className={css.sectorBreak} />}
            <div className={css.fieldContainer}>
                <Label
                    className={getFieldsLabelClass(WIDGET_COLUMNS[widget.type].mandatory)}
                    text={WIDGET_COLUMNS[widget.type].name}
                />
                {!WIDGET_COLUMNS[widget.type].single && dragTooltip}
                <NewTagInput
                    placeholder="STATIC.PLACEHOLDERS.SELECT_DIMENSION"
                    containerStyle={{ width: '100%' }}
                    tags={selectedColumnsUI}
                    options={allDimensions.filter(x => !widget.options.rows.includes(x.value))}
                    sortable
                    disabled={breakdownsDisabled}
                    isMulti={!WIDGET_COLUMNS[widget.type].single}
                    isClearable={!!WIDGET_COLUMNS[widget.type].single}
                    onChange={selected => {
                        if (WIDGET_COLUMNS[widget.type].single) {
                            const value = Array.isArray(selected)
                                ? selected[selected.length - 1]?.value
                                : selected?.value;
                            setWidgetOptions({ columns: value ? [value] : [] });
                        } else {
                            setWidgetOptions({ columns: (selected || []).map(tag => tag.value) });
                        }
                    }}
                    error={breakdownColumnsError || (errorComponent === SUBCOMPONENTS.columns && error ? error : '')}
                />
            </div>
        </>
    );

    const showCreativePreviewCheckbox = useMemo(() => shouldShowCreativePreviewCheckbox(widget.type, widget.dataType), [
        widget.type,
        widget.dataType,
    ]);

    const showCreativePreview = showCreativePreviewCheckbox && !!widget.query.creativeGroupBy?.length;

    // table/pivot are the widget types whose onSave path persists options; heatMapTable still
    // auto-persists widths on resize but has no shelf save branch, so no opt-out checkbox for it.
    const isTableLikeWidget = ['table', 'pivot'].includes(widget.type);
    const persistColumnWidths = widget.options.persistColumnWidths !== false;

    return (
        <Shelf
            open={shelfOpen}
            shelfSize="medium"
            headerText={`${TRANSLATION_PREFIX}.HEADER_ADD`}
            onClose={useCallback(() => {
                const formDirty = widget !== originalWidget.current;
                if (formDirty && !confirm(translate('STATIC.PAGES.CUSTOM_EVENTS.UNSAVED_CHANGES_WARNING'))) {
                    return;
                }
                trackDashboardsWidgetOpenedMixpanelEvent({
                    dashboard: null,
                    widget,
                    changeType: null,
                    isSaved: false,
                });
                setError('');
                close();
            })}
            footerNavigationButtons={[
                {
                    type: 'cancelButton',
                    text: 'Cancel',
                },
                {
                    type: 'primaryButton',
                    text: 'Save',
                    onClick: onSave,
                    disabled: !!error,
                },
            ]}
            enterAnimationDisabled={false}
        >
            {!apiFields && (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Spinner show />
                </div>
            )}
            {apiFields && (
                <div className={css.shelfContainer}>
                    <div className={css.topSectorContainer}>
                        <div className={css.topSectorDataContainer}>
                            <div className={css.fieldContainer}>
                                <Label className={`${css.fieldLabel} ${css.fieldLabelRequired}`} text="Widget Name" />
                                <TextField
                                    inputConfig={{ autoFocus: true }}
                                    placeholder="Enter widget name"
                                    onChange={name => setWidget(prevWidget => ({ ...prevWidget, name }))}
                                    value={widget.name}
                                    error={errorComponent === SUBCOMPONENTS.name && error ? error : ''}
                                />
                            </div>
                            <div className={css.fieldContainer}>
                                <Label
                                    className={`${css.fieldLabel} ${css.fieldLabelRequired}`}
                                    text="Reporting Type"
                                />
                                <Dropdown
                                    items={availableDataTypes.current}
                                    selected={WIDGET_DATA_TYPES[widget.dataType]}
                                    disabled={!!preconfiguredType}
                                    onSelection={dataType =>
                                        setWidget(prevWidget => {
                                            const query = { ...fieldsEmptyState.query };
                                            if (shouldShowCreativePreviewCheckbox(prevWidget.type, dataType.name)) {
                                                query.creativeGroupBy = [IMAGE_GROUP_BY];
                                            }

                                            const newWidgetQuery = {
                                                ...(prevWidget.query || {}),
                                                ...query,
                                            };
                                            const newWidgetOptions = {
                                                ...(prevWidget.options || {}),
                                                ...fieldsEmptyState.options,
                                            };

                                            return {
                                                ...prevWidget,
                                                dataType: dataType.name,
                                                query: newWidgetQuery,
                                                options: newWidgetOptions,
                                            };
                                        })
                                    }
                                    wrapperStyle={{ width: '100%' }}
                                    containerStyle={{ width: '100%' }}
                                />
                            </div>
                            <div className={css.fieldContainer}>
                                <Label className={`${css.fieldLabel} ${css.fieldLabelRequired}`} text="Widget Type" />
                                <AutoComplete
                                    isCreatable={false}
                                    controlledValue={{ value: widget.type, label: WIDGET_TYPES[widget.type] }}
                                    onChange={option =>
                                        setWidget(prevWidget => {
                                            const newType = option.value;
                                            const query = { ...fieldsEmptyState.query, ...(prevWidget.query || {}) };
                                            const options = {
                                                ...fieldsEmptyState.options,
                                                ...(prevWidget.options || {}),
                                            };
                                            if (shouldShowCreativePreviewCheckbox(newType, widget.dataType)) {
                                                query.creativeGroupBy = [IMAGE_GROUP_BY];
                                            }

                                            if (!WIDGET_DIMENSIONS[newType]) {
                                                query.dimensions = [];
                                            }

                                            if (!WIDGET_ROWS[newType]) {
                                                options.rows = [];
                                            }

                                            if (!WIDGET_COLUMNS[newType]) {
                                                options.columns = [];
                                            }

                                            if (WIDGET_ROWS[newType]?.single && options.rows.length > 1) {
                                                options.rows = options.rows.slice(0, 1);
                                            }

                                            if (WIDGET_COLUMNS[newType]?.single && options.columns.length > 1) {
                                                options.columns = options.columns.slice(0, 1);
                                            }

                                            // Lead the user with a sensible default X axis for heatmaps.
                                            if (newType === HEATMAP_WIDGET && !options.rows.length) {
                                                options.rows = [DAY_DIMENSION];
                                            }

                                            const heatmapOptions =
                                                newType === HEATMAP_WIDGET
                                                    ? prevWidget.heatMapChartOptions || defaultHeatmapOptions()
                                                    : undefined;

                                            return {
                                                ...prevWidget,
                                                type: newType,
                                                query,
                                                options,
                                                heatMapChartOptions: heatmapOptions,
                                            };
                                        })
                                    }
                                    selectOptions={{
                                        isSearchable: false,
                                        options: WIDGET_TYPE_OPTIONS,
                                    }}
                                />
                            </div>
                        </div>
                        <div className={css.topSectorPreviewContainer}>
                            <Label className={`${css.fieldLabel} ${css.previewLabel}`} text="Preview:" />
                            {widget.type === WIDGET_TYPE_TOTALS ? (
                                <WIDGET_TYPE_TO_PREVIEW.totals />
                            ) : (
                                <img src={WIDGET_TYPE_TO_PREVIEW[widget.type]} />
                            )}
                        </div>
                    </div>
                    <div className={css.sectorBreak} />
                    {(widget.dataType === DataTypes.SKAN || widget.dataType === DataTypes.SKAN_RAW) && (
                        <div className={css.fieldContainer}>
                            <Label className={getFieldsLabelClass(true)} text="Skan Date Type" />
                            <AutoComplete
                                isCreatable={false}
                                controlledValue={{
                                    value: widget.query.skanDateDimensionName,
                                    label: SKAN_DATE_TYPES[widget.query.skanDateDimensionName],
                                }}
                                onChange={option => updateWidgetQueryFields({ skanDateDimensionName: option.value })}
                                selectOptions={{
                                    isSearchable: false,
                                    options: DATE_TYPE_OPTIONS,
                                }}
                            />
                        </div>
                    )}
                    {showCreativePreviewCheckbox && (
                        <div className={css.fieldContainer}>
                            <Checkbox
                                labelTooltip={`${TRANSLATION_PREFIX}.CREATIVE_PREVIEW.LABEL_TITLE`}
                                size="small"
                                variant={CheckboxVariants.DARK}
                                labelClass={css.checkbox}
                                label={<Translate id={`${TRANSLATION_PREFIX}.CREATIVE_PREVIEW.LABEL`} />}
                                checked={showCreativePreview}
                                onChange={() =>
                                    updateWidgetQueryFields({
                                        creativeGroupBy: showCreativePreview ? [] : [IMAGE_GROUP_BY],
                                    })
                                }
                            />
                        </div>
                    )}
                    {WIDGET_DIMENSIONS[widget.type] && (
                        <div className={css.fieldContainer}>
                            <Label
                                className={getFieldsLabelClass(WIDGET_DIMENSIONS[widget.type].mandatory)}
                                text={WIDGET_DIMENSIONS[widget.type].name}
                            />
                            {dragTooltip}
                            <NewTagInput
                                placeholder="STATIC.PLACEHOLDERS.SELECT_DIMENSION"
                                containerStyle={{ width: '100%' }}
                                tags={selectedDimensionsUI}
                                options={allDimensions}
                                sortable
                                onChange={tags => updateWidgetQueryFields({ dimensions: tags.map(x => x.value) })}
                                error={errorComponent === SUBCOMPONENTS.dimensions && error ? error : ''}
                            />
                        </div>
                    )}
                    {widget.dataType === DataTypes.CROSS_DEVICE && (
                        <div className={css.fieldContainer}>
                            <Label className={getFieldsLabelClass(true)} text="Attribution Type" />
                            <NewTagInput
                                placeholder="Select Attribution Type"
                                containerStyle={{ width: '100%' }}
                                tags={crossDeviceDataTypesUI}
                                options={crossDeviceDataTypes}
                                onChange={tags => updateWidgetQueryFields({ dataTypeFields: tags.map(x => x.value) })}
                                error={errorComponent === SUBCOMPONENTS.crossDeviceDataType && error ? error : ''}
                            />
                        </div>
                    )}
                    {widget.dataType === DataTypes.UNIFIED && (
                        <div className={css.fieldContainer}>
                            <Label className={getFieldsLabelClass(true)} text="Data Type" />
                            <NewTagInput
                                placeholder="Select Data Type"
                                containerStyle={{ width: '100%' }}
                                tags={selectedDataTypesUI}
                                options={unifiedDataTypes}
                                onChange={tags => updateWidgetQueryFields({ dataTypeFields: tags.map(x => x.value) })}
                                error={errorComponent === SUBCOMPONENTS.unifiedDataType && error ? error : ''}
                            />
                        </div>
                    )}
                    {WIDGET_ROWS[widget.type] && (
                        <div className={css.fieldContainer}>
                            <Label
                                className={getFieldsLabelClass(WIDGET_ROWS[widget.type].mandatory)}
                                text={WIDGET_ROWS[widget.type].name}
                            />
                            {!WIDGET_ROWS[widget.type].single && dragTooltip}
                            <NewTagInput
                                placeholder="STATIC.PLACEHOLDERS.SELECT_DIMENSION"
                                containerStyle={{ width: '100%' }}
                                tags={selectedRowsUI}
                                options={allDimensions.filter(x => !widget.options.columns.includes(x.value))}
                                sortable
                                isMulti={!WIDGET_ROWS[widget.type].single}
                                isClearable={!!WIDGET_ROWS[widget.type].single}
                                onChange={selected => {
                                    if (WIDGET_ROWS[widget.type].single) {
                                        const value = Array.isArray(selected)
                                            ? selected[selected.length - 1]?.value
                                            : selected?.value;
                                        setWidgetOptions({ rows: value ? [value] : [] });
                                    } else {
                                        setWidgetOptions({ rows: (selected || []).map(tag => tag.value) });
                                    }
                                }}
                                error={errorComponent === SUBCOMPONENTS.rows && error ? error : ''}
                            />
                        </div>
                    )}
                    {WIDGET_COLUMNS[widget.type] &&
                        (WIDGET_COLUMNS[widget.type].mandatory || isPivotWidget) &&
                        widgetColumns}
                    <div className={css.fieldContainer}>
                        <Label
                            className={getFieldsLabelClass(WIDGET_METRICS[widget.type].mandatory)}
                            text={WIDGET_METRICS[widget.type].name}
                        />
                        {!isChartWidget && !isSingleMetricWidget && dragTooltip}
                        <NewTagInput
                            placeholder={
                                isMultiMetricsChartWidget
                                    ? `${TRANSLATION_PREFIX}.PLACEHOLDERS.SELECT_METRICS`
                                    : 'STATIC.PLACEHOLDERS.SELECT_METRIC'
                            }
                            containerStyle={{ width: '100%' }}
                            tags={selectedMetricsUI}
                            options={allMetrics}
                            sortable
                            isMulti={isMultipleMetricsWidget}
                            isClearable
                            onChange={onMetricsChange}
                            error={metricsError || (errorComponent === SUBCOMPONENTS.metrics && error ? error : '')}
                        />
                    </div>
                    {widget.type === HEATMAP_WIDGET && (
                        <div className={css.fieldContainer}>
                            <HeatmapColorConfig
                                value={widget.heatMapChartOptions || defaultHeatmapOptions()}
                                onChange={newHeatmapOptions => setHeatmapOptions(newHeatmapOptions)}
                                error={errorComponent === SUBCOMPONENTS.heatmapColor ? error : ''}
                            />
                        </div>
                    )}
                    {isRangeAxisChart(widget.type) && widget.query.metrics.length > 0 && (
                        <div className={css.fieldContainer}>
                            {errorComponent === SUBCOMPONENTS.axis && error && (
                                <div className={css.error}>
                                    <Translate id={error} />
                                </div>
                            )}
                            <AxisSettings
                                chartType={widget.type}
                                multiMetric={widget.query.metrics.length > 1 && widget.options.columns.length === 0}
                                metrics={axisMetrics}
                                value={widget.options.axis}
                                onChange={axis => setWidgetOptions({ axis })}
                            />
                        </div>
                    )}
                    {WIDGET_COLUMNS[widget.type] &&
                        !(WIDGET_COLUMNS[widget.type].mandatory || isPivotWidget) &&
                        widgetColumns}
                    {(!WIDGET_COLUMNS[widget.type] || WIDGET_COLUMNS[widget.type].mandatory || isPivotWidget) && (
                        <div className={css.sectorBreak} />
                    )}
                    {isTableLikeWidget && (
                        <div className={css.fieldContainer}>
                            <Checkbox
                                size="small"
                                variant={CheckboxVariants.DARK}
                                labelClass={css.checkbox}
                                label={<Translate id="STATIC.PAGES.REPORTS.PERSIST_COLUMN_WIDTHS" />}
                                checked={persistColumnWidths}
                                onChange={() => setWidgetOptions({ persistColumnWidths: !persistColumnWidths })}
                            />
                        </div>
                    )}
                    <div className={css.fieldContainer}>
                        <Label className={css.fieldLabel} text="Filters" />
                        <FilterFields
                            fields={allUnusedFilters}
                            filters={selectedFiltersWithTags}
                            operators={OPERATOR_TYPES}
                            onFieldSelection={onFieldSelection}
                            onOperatorSelection={onOperatorSelection}
                            onFilterValueAdded={onFilterValueAdded}
                            onFilterValueRemoved={onFilterValueRemoved}
                            onAddFilter={onAddFilter}
                            onRemoveFilter={onRemoveFilter}
                        />
                    </div>
                </div>
            )}
        </Shelf>
    );
}

const WIDGET_SHAPE = PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOf([
        'table',
        'pivot',
        WIDGET_TYPE_TOTALS,
        HEATMAP_WIDGET,
        ...CUSTOM_DASHBOARD_CHART_TYPES.map(x => x.name),
    ]).isRequired,
    dataType: PropTypes.oneOf(Object.values(WIDGET_DATA_TYPES).map(dataType => dataType.name)).isRequired,
    query: PropTypes.shape({
        dimensions: PropTypes.arrayOf(PropTypes.string).isRequired,
        metrics: PropTypes.arrayOf(PropTypes.string).isRequired,
        creativeGroupBy: PropTypes.arrayOf(PropTypes.string),
        filters: PropTypes.arrayOf(
            PropTypes.shape({
                field: PropTypes.string.isRequired,
                operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
                values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
            })
        ).isRequired,
    }).isRequired,
    options: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.string),
        columns: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
});

WidgetShelf.propTypes = {
    adminMode: PropTypes.bool.isRequired,
    userData: PropTypes.objectOf(PropTypes.any).isRequired,
    widgets: PropTypes.arrayOf(WIDGET_SHAPE).isRequired,
    widget: WIDGET_SHAPE.isRequired,
    onUpdateWidget: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    apiFields: PropTypes.objectOf(PropTypes.any),
    translate: PropTypes.func,
    preconfiguredType: PropTypes.string,
};

export default connect((state, ownProps) => ({
    adminMode: getAdminModeEnabled(state),
    userData: getUserData(state),
    ...ownProps,
}))(withLocalize(WidgetShelf));
